import { LastPage, LastPageNavType } from "./types";
import {
  getEventActionName,
  getStartActionName,
  getSuccessActionName,
} from "../../core/make-action";
import ViewerMethod from "../../core/ViewerMethod";
import ViewerEvent from "../../core/ViewerEvent";
import { LAST_PAGE_TOGGLE } from "./actions";
import { BookmarkData } from "../bookmarks/types";
import { CFI } from "epubjs";
import { safeParseCfi } from "../../../../../../common/utils/cfi";
import { VBKType } from "../bookinfo/types";
import { FigureRawData } from "../figures/types";
import { SearchResultRawData } from "../search/types";
import { getApiMetaData } from "../../core/ViewerApiMetaData";

function bookType(state = null, action): VBKType | null {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.GET_BOOK_INFO):
      return action.resultData.vbkType;
    default:
      return state;
  }
}

function currentLoc(state = "", action, bookType: VBKType) {
  switch (action.type) {
    case getSuccessActionName(ViewerMethod.GET_UNIVERSAL_URL):
      const url = action.resultData.url;
      if (url) {
        if (bookType === VBKType.EPUB) {
          const cfiBreak = "epubcfi";
          const pos = url.indexOf(cfiBreak);
          if (pos !== -1) {
            return url.substr(pos + cfiBreak.length);
          }
        } else {
          // pbk
          return "/" + url.split("/").pop();
        }
      }
      return url;
    default:
      return state;
  }
}

const makeLastPageRecord = (
  state: LastPage | null,
  bookType: VBKType | null,
  srcCfi: string,
  dest: string,
  destType: LastPageNavType,
  srcRelatedCfi?: string
): LastPage | null => {
  if (destType === LastPageNavType.CFI && bookType === VBKType.EPUB) {
    // note using parser to drop assertions on compare
    const parsedSrcCfi = safeParseCfi(srcCfi);
    // sometimes dest cfis come without ! and are really just the content doc
    // parsing cfis like that lead to null content doc
    const parsedDestCfi = safeParseCfi(
      dest.indexOf("!") === -1 ? dest + "!" : dest
    );
    if (!parsedSrcCfi?.contentDoc || !parsedDestCfi?.contentDoc) {
      // better to skip back to page than fall over
      return state;
    } else {
      if (CFI.equals(parsedSrcCfi.contentDoc, parsedDestCfi.contentDoc)) {
        // nav in same content doc e.g. toc items pointing at sections of same doc
        // this is back to page not back to anchor
        return state;
      }
    }
  } else if (
    bookType === VBKType.PBK &&
    destType === LastPageNavType.CFI &&
    srcCfi === dest
  ) {
    // same page so skip
    return state;
  }
  return {
    srcCfi,
    srcRelatedCfi,
    dest,
    destType,
  };
};

function lastPage(
  state: LastPage | null = null,
  action,
  bookType: VBKType | null,
  currentLoc: string
): LastPage | null {
  switch (action.type) {
    case getStartActionName(ViewerMethod.GO_TO_SEARCH_RESULT): {
      const result: SearchResultRawData = action.apiCallArgs[0];
      if (result.cfi && currentLoc) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          result.cfi,
          LastPageNavType.CFI
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_FIGURE): {
      const figure: FigureRawData = action.apiCallArgs[0];
      if (figure.location && currentLoc) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          figure.location,
          LastPageNavType.URL
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_BOOKMARK): {
      const bookmark: BookmarkData = action.apiCallArgs[0];
      let bookmarkCfi;
      if (bookType === VBKType.EPUB) {
        bookmarkCfi = bookmark.epubCfi;
      } else {
        bookmarkCfi = "/" + bookmark.pbkPage;
      }
      if (bookmarkCfi && currentLoc) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          bookmarkCfi,
          LastPageNavType.CFI
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_HIGHLIGHT): {
      if (getApiMetaData(action)?.highlightCfi && currentLoc) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          getApiMetaData(action)?.highlightCfi,
          LastPageNavType.CFI
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_CFI): {
      const destCfi = action.apiCallArgs[0];
      if (destCfi && currentLoc && !getApiMetaData(action)?.dontTrack) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          destCfi,
          LastPageNavType.CFI
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_PAGE): {
      const pageCfi = getApiMetaData(action)?.pageCfi;
      if (pageCfi && currentLoc) {
        return makeLastPageRecord(
          state,
          bookType,
          currentLoc,
          pageCfi,
          LastPageNavType.CFI
        );
      }
      return state;
    }
    case getStartActionName(ViewerMethod.GO_TO_NEXT_PAGE):
    case getStartActionName(ViewerMethod.GO_TO_PREV_PAGE):
      return null;
    case getEventActionName(ViewerEvent.NAVIGATION_REQUESTED):
      if (action.eventData.type === "link") {
        const dest = action.eventData.url || action.eventData.cfi;
        if (dest && currentLoc) {
          return makeLastPageRecord(
            state,
            bookType,
            currentLoc,
            dest,
            action.eventData.cfi ? LastPageNavType.CFI : LastPageNavType.URL,
            action.eventData.srcRelatedCfi
          );
        }
      }
      return state;
    case LAST_PAGE_TOGGLE:
      if (state) {
        return {
          ...state,
          isToggled: !state.isToggled,
        };
      }
      return state;
    default:
      return state;
  }
}

let currentLocState = "";
let bookTypeState;

export default (state = null, action): LastPage | null => {
  // custom cross slice reducer as outside of combineReducers standard use case
  // if use case becomes prevalent consider dissemination from root reducer
  bookTypeState = bookType(bookTypeState, action);
  currentLocState = currentLoc(currentLocState, action, bookTypeState);
  return lastPage(state, action, bookTypeState, currentLocState);
};
